import { ContextMenuButton, ContextMenuItem, TableHeader } from 'redesign';
import type { LevelTableColumn } from 'redesign';
import { Box, styled, Tooltip, Typography } from '@mui/material';
import { TFunctionNonStrict } from 'i18next';
import {
  BankAccountTableCell,
  BankAccountTableData,
  BankAccountTableOptions,
} from '../bank-account-table.types';

export const getBankAccountColumnDefinition = (
  showEmploymentId: boolean,
  t: TFunctionNonStrict<'hq-screen-specific', undefined>
) => {
  const workerName: LevelTableColumn<BankAccountTableData, BankAccountTableOptions> = {
    Header: () => (
      <TableHeader>
        {t('bank_account.table_headers.name', { ns: 'hq-screen-specific' })}
      </TableHeader>
    ),
    accessor: 'workerName',
    Cell: SimpleCell,
  };
  const iban: LevelTableColumn<BankAccountTableData, BankAccountTableOptions> = {
    Header: () => (
      <TableHeader>
        {t('bank_account.table_headers.account_number', { ns: 'hq-screen-specific' })}
      </TableHeader>
    ),
    width: 300,
    accessor: 'iban',
    Cell: SimpleCell,
  };
  const previousIban: LevelTableColumn<BankAccountTableData, BankAccountTableOptions> = {
    Header: (
      <TableHeader>
        {t('bank_account.table_headers.old_account_number', { ns: 'hq-screen-specific' })}
      </TableHeader>
    ),
    width: 300,
    accessor: 'previousIban',
    Cell: SimpleCell,
  };
  const employmentId: LevelTableColumn<BankAccountTableData, BankAccountTableOptions> = {
    Header: (
      <TableHeader>
        {t('bank_account.table_headers.employee_number', { ns: 'hq-screen-specific' })}
      </TableHeader>
    ),
    width: 150,
    accessor: 'employmentId',
    Cell: SimpleCell,
  };
  const actions: LevelTableColumn<BankAccountTableData, BankAccountTableOptions> = {
    accessor: 'actions',
    Header: '',
    width: 89,
    Cell: ActionCell,
  };

  const changedWithinInterval: LevelTableColumn<BankAccountTableData, BankAccountTableOptions> = {
    accessor: 'changedWithinInterval',
    Header: '',
    width: 60,
    Cell: FlagCell,
  };

  return [
    workerName,
    iban,
    previousIban,
    ...(showEmploymentId ? [employmentId] : []),
    actions,
    changedWithinInterval,
  ];
};

const SimpleCell = (
  cell:
    | BankAccountTableCell<'workerName'>
    | BankAccountTableCell<'iban'>
    | BankAccountTableCell<'previousIban'>
) => {
  return (
    <Typography noWrap variant="subtitle2">
      {cell.value}
    </Typography>
  );
};

const ActionCell = (cell: BankAccountTableCell<'actions'>) => {
  const { approve, reject, value } = cell;

  return (
    <ContextMenuButton menuId={`bank-account-actions-${value.id}`}>
      <ContextMenuItem key="approve" onClick={() => approve(value.id)}>
        Goedkeuren
      </ContextMenuItem>
      <ContextMenuItem key="reject" onClick={() => reject(value.id)}>
        Afkeuren
      </ContextMenuItem>
    </ContextMenuButton>
  );
};

const FlagCell = (cell: BankAccountTableCell<'changedWithinInterval'>) => {
  return (
    <Tooltip placement="right" title={'Voorgestelde update binnen een bepaald interval'}>
      <Box display="flex" alignItems="center">
        {cell.value ? (
          <RedDotNotification data-testid="bank-account-changed-within-interval" />
        ) : null}
      </Box>
    </Tooltip>
  );
};

const RedDotNotification = styled(Box)(({ theme }) => ({
  width: theme.spacing(2),
  height: theme.spacing(2),
  borderRadius: '50%',
  backgroundColor: theme.palette.lwDanger[100],
  marginBottom: theme.spacing(3),
  marginRight: theme.spacing(1),
}));
